import { ChangeEvent, useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";

import Radio from "components/shared/Form/Radio";
import Layout1 from "assets/svg/heatmap-layout/layout1.svg";
import DashboardCreateSelectSensorModal from "components/dashboard/create/modal/SelectDevices";
import HeatmapCombinedSensorList from "./HeatmapCombinedSensorList";
import HeatmapSingleSensorList from "./HeatmapSingleSensorList";
import { RemoveIcon } from "components/common";
import { showErrorAlert } from "utils/alert";
import { MAX_FILE_SIZE_MB } from "constant";
import { DeviceSelected } from "types";
import "./styles.css";
import { keyBy } from "lodash";

type RowSelectionProps = {
    children: React.ReactNode;
    onClick?: () => void;
    selected?: boolean;
};

const RowSelection = ({ children, onClick, selected }: RowSelectionProps) => {
    return (
        <button
            type="button"
            className={`create-pcs-chart-row-selection${
                selected ? " create-pcs-chart-row-selection__selected" : ""
            }`}
            onClick={() => onClick?.()}
        >
            <div>
                <Radio
                    checked={!!selected}
                    defaultChecked={false}
                    onChange={() => onClick?.()}
                />
            </div>
            <div>{children}</div>
        </button>
    );
};

export const HeatmapLayout = ({ selected }: { selected: boolean }) => {
    return (
        <RowSelection selected={!!selected}>
            <div style={{ display: "flex" }}>
                {/* Set width and height to render before the image is loaded */}
                <img src={Layout1} alt="Layout 1" />
            </div>
        </RowSelection>
    );
};

type CreatePeopleHeatmapChartsProps = {
    onChange?: (data: {
        displayType: number;
        heatmapLayout: number;
        singleChartList: any[];
        chartName: string;
        combinedSensorList?: DeviceSelected[];
        uploadImage: File | null;
    }) => void;
};

const CreatePeopleHeatmapCharts = ({
    onChange,
}: CreatePeopleHeatmapChartsProps) => {
    const fileRef = useRef<any>(null);
    const [displayType, setDisplayType] = useState(1);
    const [heatmapLayout] = useState(1);
    const [selectSensorModal, setSelectSensorModal] = useState<{
        show: boolean;
        multipleSelection?: boolean;
        selectedSensors?: any;
        handler: (data: Record<string, DeviceSelected>) => void;
        excludeDevices?: any[];
    } | null>(null);
    const [singleChartList, setSingleChartList] = useState<DeviceSelected[]>(
        []
    );
    const sensorType = "People Count Sensor";
    const [widgetName, setWidgetName] = useState("People Count Heatmap");
    const [combinedSensorList, setCombinedSensorList] =
        useState<DeviceSelected[]>();
    const [uploadImage, setUploadImage] = useState<File | null>(null);

    useEffect(() => {
        onChange?.({
            displayType,
            heatmapLayout,
            singleChartList,
            chartName: widgetName,
            combinedSensorList,
            uploadImage,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        displayType,
        heatmapLayout,
        singleChartList,
        widgetName,
        combinedSensorList,
        uploadImage,
    ]);

    const removeFileUpload = () => {
        fileRef.current.value = "";
        setUploadImage(null);
    };

    const handleChangeUploadImage = (e: ChangeEvent<any>) => {
        const file = e.target.files?.[0];

        if (!(file instanceof File)) {
            removeFileUpload();
            return;
        }

        const fileSize = file.size / 1024 / 1024;
        if (fileSize > MAX_FILE_SIZE_MB) {
            showErrorAlert({
                message: `Image size should be less than ${MAX_FILE_SIZE_MB} MB.`,
            });
            removeFileUpload();
            return;
        }

        setUploadImage(file);
    };

    return (
        <div>
            <p>Heatmap chart of</p>
            <div style={{ gap: 20, display: "flex", flexDirection: "column" }}>
                <RowSelection
                    onClick={() => setDisplayType(1)}
                    selected={displayType === 1}
                >
                    <div>Combined Sensor Data</div>
                    <div className="create-pcs-chart-row-selection-body">
                        Plot data from multiple people count sensors onto a
                        single chart for a comprehensive overview.
                    </div>
                </RowSelection>

                <RowSelection
                    onClick={() => setDisplayType(2)}
                    selected={displayType === 2}
                >
                    <div>Individual Sensor Data</div>
                    <div className="create-pcs-chart-row-selection-body">
                        Create separate charts for each people count sensor,
                        allowing detailed analysis of individual sensor data.
                    </div>
                </RowSelection>
            </div>

            {displayType === 1 && (
                <>
                    <Form.Group className="mt-4">
                        <Form.Label htmlFor="widget-name">
                            Widget Name
                        </Form.Label>
                        <Form.Control
                            id="widget-name"
                            name="widget-name"
                            type="text"
                            defaultValue={""}
                            placeholder=""
                            value={widgetName}
                            onChange={(event) =>
                                setWidgetName(event.target.value)
                            }
                        />
                    </Form.Group>

                    <Form.Group className="mt-4">
                        <Form.Label>Heatmap Layout</Form.Label>
                        <div style={{ display: "flex" }}>
                            <HeatmapLayout selected={heatmapLayout === 1} />
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-4">
                        <Form.Label htmlFor="upload-layout-image">
                            Upload image for selected layout
                        </Form.Label>
                        <div>
                            {!uploadImage && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        fileRef.current.click();
                                    }}
                                >
                                    Upload Layout Image
                                </Button>
                            )}
                            <Form.File
                                id="upload-layout-image"
                                ref={fileRef}
                                style={{ display: "none" }}
                                accept=".jpeg,.jpg,.png"
                                onChange={handleChangeUploadImage}
                            />

                            {uploadImage && (
                                <div
                                    style={{
                                        padding: "14px 16px",
                                        gap: 14,
                                        minWidth: 179,
                                        borderRadius: 5,
                                        display: "inline-flex",
                                        justifyContent: "space-around",
                                        alignItems: "center",
                                        background: "#394662",
                                    }}
                                >
                                    <div>{uploadImage.name}</div>
                                    <div>
                                        <Button
                                            variant="link"
                                            className="p-0 text-decoration-none d-flex"
                                            onClick={() => removeFileUpload()}
                                        >
                                            <RemoveIcon />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form.Group>

                    <div
                        className="mt-4"
                        style={{ borderTop: "1px solid #3E4B67" }}
                    ></div>

                    <Form.Group className="mt-4">
                        <Form.Label>
                            Sensors for combined heatmap chart
                        </Form.Label>
                        <HeatmapCombinedSensorList
                            onSelectSensor={(modal: any) => {
                                setSelectSensorModal(modal);
                            }}
                            onChange={(data: any) =>
                                setCombinedSensorList(data)
                            }
                            sensors={combinedSensorList}
                        />
                    </Form.Group>
                </>
            )}

            {displayType === 2 && (
                <Form.Group className="mt-4">
                    <Form.Label>
                        Sensors for individual heatmap charts
                    </Form.Label>

                    <div className="mt-4">
                        <Button
                            variant="primary"
                            onClick={() => {
                                setSelectSensorModal({
                                    show: true,
                                    multipleSelection: true,
                                    selectedSensors: keyBy(
                                        singleChartList,
                                        "deviceId"
                                    ),
                                    handler: (
                                        data: Record<string, DeviceSelected>
                                    ) => {
                                        const sensors = Object.values(data).map(
                                            (c) => ({
                                                ...c,
                                                chartName: c.sensorName,
                                            })
                                        );
                                        setSingleChartList(sensors);
                                    },
                                });
                            }}
                        >
                            Select Sensors
                        </Button>
                    </div>

                    <HeatmapSingleSensorList
                        onChange={(data: any) => setSingleChartList(data)}
                        sensors={singleChartList}
                    />
                </Form.Group>
            )}

            <DashboardCreateSelectSensorModal
                allowSensorFilter={false}
                multipleSelection={!!selectSensorModal?.multipleSelection}
                show={!!selectSensorModal}
                deviceType="Sensor"
                onHide={() => setSelectSensorModal(null)}
                values={selectSensorModal?.selectedSensors}
                excludeDevices={selectSensorModal?.excludeDevices || []}
                sensorTypes={[sensorType]}
                onSave={(event, data) => {
                    selectSensorModal?.handler(data);
                    setSelectSensorModal(null);
                }}
            />
        </div>
    );
};

export default CreatePeopleHeatmapCharts;
